import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play } from "lucide-react";

/**
 * Cinematic auto-cycling "video-like" showcase of all Ashvolt projects.
 * Ken Burns zoom + cross-fade + title reveal + progress bar.
 */
export default function ProjectsShowcase({ data }) {
  // Build scenes from site data
  const scenes = [
    {
      id: "game",
      eyebrow: "01 — Il Gioco",
      title: data.releases[0].title,
      caption: data.releases[0].summary,
      image: data.releases[0].cover,
    },
    {
      id: "gadgets",
      eyebrow: "02 — Gadget Ashvolt",
      title: "Gadget in arrivo",
      caption: "Poster numerati, mug in metallo, portachiavi, pin smaltati e taccuini.",
      image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_rockstar-forge-1/artifacts/8d3m57ot_WhatsApp%20Image%202026-08-03%20at%2021.43.43.jpeg",
    },
    {
      id: "apparel",
      eyebrow: "03 — Magliette & Apparel",
      title: "Store magliette",
      caption: "Felpe oversize, t-shirt A★, cappellini ricamati, longsleeve.",
      image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_rockstar-forge-1/artifacts/fd40izig_WhatsApp%20Image%202026-08-03%20at%2018.59.42.jpeg",
    },
    {
      id: "dlc",
      eyebrow: "04 — DLC",
      title: "Espansioni in scrittura",
      caption: "Tre contenuti aggiuntivi in valutazione per il 2027.",
      image: "https://images.unsplash.com/photo-1590142035743-0ffa020065e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxkYXJrJTIwY2luZW1hdGljJTIwbW9vZHklMjBnYW1lJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4NTc2OTg3M3ww&ixlib=rb-4.1.0&q=85",
    },
    {
      id: "store",
      eyebrow: "05 — Ashvolt Store",
      title: "Un solo store, due aperture",
      caption: "Preordini gioco 20 maggio 2027 · Gadget & magliette 15 agosto 2027.",
      image: "https://images.unsplash.com/photo-1475070929565-c985b496cb9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxkYXJrJTIwY2luZW1hdGljJTIwbW9vZHklMjBnYW1lJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4NTc2OTg3M3ww&ixlib=rb-4.1.0&q=85",
    },
  ];

  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const SCENE_MS = 4800;

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % scenes.length), SCENE_MS);
    return () => clearTimeout(t);
  }, [idx, paused, scenes.length]);

  const scene = scenes[idx];

  return (
    <section
      id="showreel"
      data-testid="projects-showcase"
      className="relative border-y border-white/10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative w-full aspect-[16/9] md:aspect-[21/9] max-h-[90vh] overflow-hidden">
        {/* Slides */}
        <AnimatePresence mode="popLayout">
          <motion.div
            key={scene.id}
            initial={{ opacity: 0, scale: 1.15 }}
            animate={{ opacity: 1, scale: 1.05 }}
            exit={{ opacity: 0, scale: 1 }}
            transition={{ opacity: { duration: 1.2 }, scale: { duration: 6, ease: "linear" } }}
            className="absolute inset-0"
          >
            <img
              src={scene.image}
              alt={scene.title}
              className="w-full h-full object-cover"
            />
          </motion.div>
        </AnimatePresence>

        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/30" />
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, #ffffff 0, #ffffff 1px, transparent 1px, transparent 3px)",
          }}
        />

        {/* Top row: label + timecode */}
        <div className="absolute top-6 md:top-8 left-6 md:left-12 right-6 md:right-12 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            <span className="font-body text-[10px] md:text-xs uppercase tracking-[0.35em] text-accent">
              Showreel — Ashvolt 2027
            </span>
          </div>
          <div className="hidden md:flex items-center gap-2 border border-white/15 bg-black/40 backdrop-blur-sm px-3 py-1.5">
            <Play size={11} className="text-accent fill-accent" />
            <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/70">
              {String(idx + 1).padStart(2, "0")} / {String(scenes.length).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-14 md:pb-20 max-w-[1600px] mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={scene.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="font-body text-[11px] md:text-xs uppercase tracking-[0.35em] text-accent mb-4">
                {scene.eyebrow}
              </div>
              <h3 className="font-heading uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-4 max-w-4xl">
                {scene.title}
              </h3>
              <p className="font-body text-sm md:text-lg text-white/70 max-w-2xl leading-relaxed">
                {scene.caption}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress indicators */}
        <div className="absolute bottom-6 md:bottom-8 left-6 md:left-12 right-6 md:right-12 flex items-center gap-2">
          {scenes.map((s, i) => (
            <button
              key={s.id}
              type="button"
              data-testid={`showcase-dot-${s.id}`}
              onClick={() => setIdx(i)}
              className="group flex-1 h-1 relative overflow-hidden"
              aria-label={`Vai a ${s.title}`}
            >
              <span className="absolute inset-0 bg-white/10" />
              <span
                className={`absolute inset-0 bg-accent transition-transform origin-left ${
                  i < idx ? "scale-x-100" : i === idx ? "" : "scale-x-0"
                }`}
                style={
                  i === idx
                    ? {
                        animation: paused
                          ? "none"
                          : `progressFill ${SCENE_MS}ms linear forwards`,
                      }
                    : undefined
                }
              />
            </button>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes progressFill {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
    </section>
  );
}
